import { toast } from './toast';

// Error types that should NOT be shown in toasts
const SILENT_ERROR_TYPES = [
  'UNAUTHORIZED', // 401 - handled separately in AuthContext
  'FORBIDDEN', // 403 - can be handled in components
  'NOT_FOUND', // 404 - can be handled in routing
  'VALIDATION_ERROR', // Validation errors - shown in forms
  'SESSION_EXPIRED', // Session expiry - handled by session UI
] as const;

// Error types that should be shown as warning
const WARNING_ERROR_TYPES = [
  'FILE_TOO_LARGE',
  'INVALID_FILE_TYPE',
  'QUOTA_EXCEEDED',
  'RATE_LIMIT_EXCEEDED',
];

// Error types that should be shown as info
const INFO_ERROR_TYPES = ['PARTIAL_SUCCESS', 'NO_FILES_SELECTED', 'EMPTY_FILE'];

interface BackendError {
  code?: string;
  message: string;
  details?: string;
  status?: number;
}

interface ErrorHandlerOptions {
  showToast?: boolean;
  fallbackMessage?: string;
}

/**
 * Handles backend errors and shows them in toasts
 */
export const handleBackendError = (
  error: BackendError | Error | unknown,
  options: ErrorHandlerOptions = {}
) => {
  const {
    showToast = true,
    fallbackMessage = 'An unexpected error occurred',
  } = options;

  // Normalize error
  let normalizedError: BackendError;

  if (error instanceof Error) {
    normalizedError = {
      message: error.message,
      code: 'UNKNOWN_ERROR',
    };
  } else if (typeof error === 'object' && error !== null) {
    normalizedError = error as BackendError;
  } else {
    normalizedError = {
      message: String(error),
      code: 'UNKNOWN_ERROR',
    };
  }

  // Log to console if needed
  // if (logToConsole) {
  //   console.error('[ErrorHandler] Backend error:', normalizedError);
  // }

  // Check if toast should be shown
  if (!showToast) {
    return normalizedError;
  }

  // Check exceptions
  if (
    normalizedError.code &&
    (SILENT_ERROR_TYPES as readonly string[]).includes(normalizedError.code)
  ) {
    return normalizedError;
  }

  // Silence "Session expired - redirecting to login" message (it's expected behavior)
  if (
    normalizedError.message &&
    normalizedError.message.includes('Session expired - redirecting to login')
  ) {
    return normalizedError;
  }

  // Also silence token-related errors on login page
  const isOnLoginPage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/login' ||
      window.location.pathname === '/' ||
      window.location.pathname.startsWith('/login'));

  if (isOnLoginPage && normalizedError.message) {
    const isTokenError =
      normalizedError.message.includes('Missing token') ||
      normalizedError.message.includes('Unauthorized') ||
      normalizedError.message.includes('Please provide a valid token');

    if (isTokenError) {
      return normalizedError;
    }
  }

  if (normalizedError.message) {
    const isAuthFetchNoise =
      normalizedError.message.includes('Failed to fetch user') ||
      normalizedError.message.includes('Missing token');
    if (isAuthFetchNoise) {
      return normalizedError;
    }
  }

  // Determine toast type
  let toastType: 'error' | 'warning' | 'info' = 'error';

  if (normalizedError.code) {
    if (WARNING_ERROR_TYPES.includes(normalizedError.code)) {
      toastType = 'warning';
    } else if (INFO_ERROR_TYPES.includes(normalizedError.code)) {
      toastType = 'info';
    }
  }

  // Show toast
  const message = normalizedError.message || fallbackMessage;
  const description = normalizedError.details || undefined;

  switch (toastType) {
    case 'warning':
      toast.warning(message, { description });
      break;
    case 'info':
      toast.info(message, { description });
      break;
    case 'error':
    default:
      toast.error(message, { description });
      break;
  }

  return normalizedError;
};

/**
 * Handles API request errors
 */
export const handleApiError = (error: unknown, context?: string) => {
  const contextPrefix = context ? `[${context}]` : '';

  return handleBackendError(error, {
    showToast: true,
    fallbackMessage: `${contextPrefix} API request failed`,
  });
};

/**
 * Handles file upload errors
 */
export const handleUploadError = (error: unknown, fileName?: string) => {
  const fileInfo = fileName ? ` for file "${fileName}"` : '';

  return handleBackendError(error, {
    showToast: true,
    fallbackMessage: `Upload failed${fileInfo}`,
  });
};

/**
 * Checks if error should be shown in toast
 */
export const shouldShowErrorInToast = (error: BackendError): boolean => {
  if (!error.code) return true;
  return !(SILENT_ERROR_TYPES as readonly string[]).includes(error.code);
};

/**
 * Gets user-friendly message for error
 */
export const getErrorMessage = (error: BackendError): string => {
  // Custom messages for known errors
  const ERROR_MESSAGES: Record<string, string> = {
    FILE_TOO_LARGE: 'File size exceeds the maximum allowed limit',
    INVALID_FILE_TYPE: 'File type is not supported',
    QUOTA_EXCEEDED: 'Storage quota exceeded',
    RATE_LIMIT_EXCEEDED: 'Too many requests. Please try again later',
    PARTIAL_SUCCESS: 'Some operations completed successfully',
    NO_FILES_SELECTED: 'Please select files to upload',
    EMPTY_FILE: 'File is empty and cannot be processed',
  };

  return (
    ERROR_MESSAGES[error.code || ''] || error.message || 'An error occurred'
  );
};
